import { Suspense, lazy, useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Download, FileSpreadsheet, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { exportOriginalDocument } from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import type { DdqSessionQuestion } from '@/types/api.types';
import { useWorkspaceSession } from '../hooks/useWorkspaceSession';
import type { WorkspaceDocType, WorkspaceQuestion } from '../types';
import { DocumentWorkspace } from './DocumentWorkspace';

// Lazy-load each editor in its OWN chunk so a docx session does NOT download
// Univer and an xlsx session does NOT download the ProseMirror/jszip docx
// editor. Both sit under the already-lazy WorkspaceContainer (ReviewPage
// React.lazy-imports this module), so neither editor lands in the main bundle.
const DocxEditorPane = lazy(() =>
  import('./DocxEditorPane').then((m) => ({ default: m.DocxEditorPane })),
);
const UniverEditorPane = lazy(() =>
  import('./UniverEditorPane').then((m) => ({ default: m.UniverEditorPane })),
);

export interface WorkspaceContainerProps {
  /** The session whose original document is loaded into the live editor. */
  sessionId: string;
}

/** The MIME type for a Word document download. */
const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
/** The MIME type for an Excel spreadsheet download. */
const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** The two formats that mount a live editor (and fetch the prefilled original). */
function isEditableDocType(docType: WorkspaceDocType): docType is 'docx' | 'xlsx' {
  return docType === 'docx' || docType === 'xlsx';
}

/** Format-aware download mime + file extension for the answered export. */
function exportFormat(docType: WorkspaceDocType): { mime: string; ext: string } {
  return docType === 'xlsx' ? { mime: XLSX_MIME, ext: 'xlsx' } : { mime: DOCX_MIME, ext: 'docx' };
}

/**
 * Serialize a question's write-back anchor to a short debug string.
 *
 * xlsx → `sheet!cell`; docx paragraph → the paragraph index; table cell → the
 * `[para, row, col]` triple; pdf page → `p<n>`. Returns null when there is no
 * anchor. Selection-sync itself does NOT use this string (the docx pane
 * correlates by comment order; the xlsx pane resolves the cell directly off the
 * anchor); this is purely for display/debug surfaces that want a human-readable
 * locator.
 */
function serializeAnchor(question: DdqSessionQuestion): string | null {
  const anchor = (question as WorkspaceQuestion).anchor;
  if (!anchor) return null;
  switch (anchor.kind) {
    case 'xlsx_cell':
      return anchor.sheet ? `${anchor.sheet}!${anchor.cell ?? ''}` : (anchor.cell ?? null);
    case 'docx_paragraph':
      return anchor.paragraph_index != null ? String(anchor.paragraph_index) : null;
    case 'docx_table_cell':
      return anchor.table ? `[${anchor.table.join(',')}]` : null;
    case 'pdf_page':
      return anchor.page != null ? `p${anchor.page}` : null;
    default:
      return null;
  }
}

/**
 * The live document workspace: wires {@link useWorkspaceSession} (panel data +
 * docType) and a TanStack Query for the prefilled original document to the
 * two-pane {@link DocumentWorkspace}. For Word sessions it mounts the live
 * `DocxEditorPane`; for Excel sessions it mounts the live `UniverEditorPane`
 * (Apache-core Univer fed by SheetJS). Each editor is lazy-loaded in its own
 * chunk so only the needed one downloads. Genuinely-unsupported types (pdf /
 * other) get a graceful placeholder.
 */
export function WorkspaceContainer({ sessionId }: WorkspaceContainerProps) {
  const { questions, docType, isLoading: isSessionLoading, error: sessionError } =
    useWorkspaceSession(sessionId);

  const isEditable = isEditableDocType(docType);

  const bufferQuery = useQuery({
    // Key off the shared factory so 'proposed' and 'accepted' cache separately
    // and the filter is part of the key (was building it inline, ignoring the
    // factory + filter).
    queryKey: queryKeys.workspaceOriginalDocument(sessionId, 'proposed'),
    queryFn: () => exportOriginalDocument(sessionId, 'proposed'),
    // Both docx and xlsx load the prefilled original; pdf/other do not.
    enabled: !!sessionId && isEditable,
    staleTime: 30_000,
  });

  const activeQuestionId = useWorkspaceStore((s) => s.activeQuestionId);

  const getAnchor = useCallback(
    (question: DdqSessionQuestion) => serializeAnchor(question),
    [],
  );

  // Viewport-relative top px of each answered question's answer in the docx
  // editor, emitted by the docx pane. Drives the Q&A panel's margin-aligned
  // rows. Empty/absent → the panel falls back to its compact list. Only the
  // docx editor measures positions; xlsx/pdf leave this empty (compact-only).
  const [rowPositions, setRowPositions] = useState<Map<string, number>>(new Map());

  // Export the accepted-only answered document and trigger a browser download.
  // Mirrors the blob-download pattern in ReviewPage's export handler. The mime
  // + filename are FORMAT-AWARE (xlsx vs docx) so an Excel session downloads a
  // valid .xlsx and a Word session a valid .docx.
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      const buffer = await exportOriginalDocument(sessionId, 'accepted');
      const { mime, ext } = exportFormat(docType);
      const blob = new Blob([buffer], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `ddq_session_${sessionId}_answered_${timestamp}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Failed to export document');
    } finally {
      setIsExporting(false);
    }
  }, [sessionId, docType]);

  // --- Loading / error gates ---------------------------------------------

  const isLoading = isSessionLoading || (isEditable && bufferQuery.isPending);
  const loadError = sessionError ?? (isEditable ? (bufferQuery.error as Error | null) : null);

  if (loadError) {
    return (
      <div
        data-testid="workspace-error"
        className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center"
      >
        <AlertCircle className="h-8 w-8 text-destructive" aria-hidden="true" />
        <p className="text-sm font-medium text-foreground">
          We couldn&apos;t load this document workspace.
        </p>
        <p className="max-w-md text-xs text-muted-foreground">{loadError.message}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        data-testid="workspace-loading"
        className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">Loading document workspace…</p>
      </div>
    );
  }

  // The editor surface for the session's format: a format-aware export header
  // plus the matching lazy editor pane (each in its own chunk), or a
  // placeholder for genuinely-unsupported types.
  let editorPane: React.ReactNode;
  if (docType === 'docx') {
    editorPane = (
      <Suspense fallback={<EditorChunkFallback />}>
        <DocxEditorPane
          buffer={bufferQuery.data}
          questions={questions}
          activeQuestionId={activeQuestionId}
          onPositions={setRowPositions}
        />
      </Suspense>
    );
  } else if (docType === 'xlsx') {
    editorPane = (
      <Suspense fallback={<EditorChunkFallback />}>
        <UniverEditorPane
          buffer={bufferQuery.data}
          questions={questions}
          activeQuestionId={activeQuestionId}
        />
      </Suspense>
    );
  } else {
    editorPane = null;
  }

  const editorSlot = isEditable ? (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-end border-b border-border px-3 py-2">
        <Button size="sm" variant="outline" onClick={handleExport} disabled={isExporting}>
          {isExporting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          Export answered document
        </Button>
      </div>
      {exportError ? (
        <p className="px-3 py-1 text-xs text-destructive" role="alert">
          {exportError}
        </p>
      ) : null}
      {editorPane}
    </div>
  ) : (
    <UnsupportedPlaceholder onExport={handleExport} isExporting={isExporting} exportError={exportError} />
  );

  return (
    <DocumentWorkspace
      sessionId={sessionId}
      questions={questions}
      editorSlot={editorSlot}
      getAnchor={getAnchor}
      rowPositions={rowPositions}
    />
  );
}

/** Fallback shown while an editor's own lazy chunk downloads. */
function EditorChunkFallback() {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center p-8 text-center">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
    </div>
  );
}

interface UnsupportedPlaceholderProps {
  onExport: () => void;
  isExporting: boolean;
  exportError: string | null;
}

/**
 * The editor-slot placeholder for genuinely-unsupported originals (pdf / other)
 * that have no in-place editor. The reviewer can still download the answered
 * document.
 */
function UnsupportedPlaceholder({ onExport, isExporting, exportError }: UnsupportedPlaceholderProps) {
  return (
    <div className={cn('flex h-full flex-col items-center justify-center gap-3 p-8 text-center')}>
      <FileSpreadsheet className="h-8 w-8 text-muted-foreground/60" aria-hidden="true" />
      <p className="text-sm font-medium text-muted-foreground">
        In-place editing isn&apos;t available for this file type
      </p>
      <p className="max-w-xs text-xs text-muted-foreground/70">
        Use Download answered document to get the prefilled file.
      </p>
      <Button size="sm" variant="outline" onClick={onExport} disabled={isExporting}>
        {isExporting ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Download className="mr-2 h-4 w-4" aria-hidden="true" />
        )}
        Download answered document
      </Button>
      {exportError ? (
        <p className="text-xs text-destructive" role="alert">
          {exportError}
        </p>
      ) : null}
    </div>
  );
}

// Default export so ReviewPage can code-split the editor via React.lazy without
// pulling the editors into the main bundle. The named export above stays the
// public API for everything else (barrel re-exports, tests).
export default WorkspaceContainer;
